/**
 * Bulk API handler for B2M Tracker
 */
import { collectPixelParams } from '../utils/pixel-params.js';

export default class BulkAPI {
  constructor(tracker) {
    this.tracker = tracker;
  }
  
  /**
   * Helper to create event property objects
   * @param {string} key - Property key
   * @param {any} value - Property value
   * @returns {Object} Property object
   */
  createEventProperty(key, value) {
    if (value === undefined || value === null) return null;
    return { key, value: String(value) };
  }

  /**
   * Resolve the app_identifier value written into user_properties.
   * Uses the configured override (data-app-identifier) when set; otherwise
   * falls back to the current browser hostname (subdomain included, e.g.
   * "blog.example.com"). Lets analysts isolate traffic per web project/domain.
   * @returns {string|null}
   */
  getAppIdentifier() {
    const configured = this.tracker.config.appIdentifier;
    if (configured) return configured;
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      return window.location.hostname;
    }
    return null;
  }

  /**
   * Custom user properties set through setUserProperties(). They are
   * persisted in `b2m_user_profile`, so we stamp them onto every event's
   * user_params — this is how attributes like company_id / email /
   * application_id travel with all events, not only the one-off $identify.
   * @returns {Array<{key:string,value:string}>}
   */
  getProfileProperties() {
    const profile = this.tracker.storage.getItem('b2m_user_profile') || {};
    return Object.entries(profile).map(
      ([key, value]) => this.createEventProperty(key, value)
    );
  }

  /**
   * Get or generate a session ID - maintains consistency across requests
   * @returns {string} Session ID
   */
  generateSessionId() {
    // Check if we have a valid existing session
    const existingSessionId = this.tracker.storage.getSessionId();
    if (existingSessionId && !this.tracker.storage.isSessionExpired(this.tracker.config.sessionTimeout)) {
      return existingSessionId;
    }
    
    // Generate new session
    const timestamp = Date.now();
    const randomPart = Math.floor(Math.random() * 1000000);
    const userIdFragment = this.tracker.userId.getId().substring(0, 8);
    
    let sessionId;
    
    // Use custom session parameters if available
    if (this.tracker.config.sessionParams) {
      const params = this.tracker.config.sessionParams;
      sessionId = params.prefix || 'session';
      
      if (params.includeTimestamp) {
        sessionId += `_${timestamp}`;
      }
      
      if (params.includeUserId) {
        sessionId += `_${userIdFragment}`;
      }
      
      if (params.includeRandom) {
        sessionId += `_${randomPart}`;
      }
    } else {
      // Default format
      sessionId = `session_${timestamp}_${userIdFragment}_${randomPart}`;
    }
    
    // Store the new session
    this.tracker.storage.setSessionId(sessionId);
    this.tracker.storage.setSessionStartTime(timestamp);
    
    return sessionId;
  }
  
  
  sendEvents(useBeacon = false) {
    const queue = this.tracker.storage.getBulkQueue();
    
    if (queue.length === 0) {
      this.tracker.logger.log("No bulk events to send");
      return;
    }
    
    // If offline, don't try to send (unless using beacon on page exit)
    if (!this.tracker.isOnline && !useBeacon) {
      this.tracker.logger.log("Device is offline, bulk events will be sent when online");
      return;
    }

    // Re-entrancy guard: the queue is only cleared after the HTTP response, so a
    // second sendEvents() before that response (e.g. init-flush racing with
    // giveConsent() on a slow network) would re-transmit the same batch. Skip
    // while a normal send is in flight; beacon (unload) sends bypass this.
    if (!useBeacon && this._sendInFlight) {
      this.tracker.logger.log("Bulk send already in flight, skipping duplicate");
      return;
    }

    // Get browser details for fingerprinting
    const browserInfo = this.tracker.browserInfo.getInfo();

    // Marketing pixel identifiers (_fbp, _fbc, gclid, _ga client id).
    // Read fresh per batch — cookies set by Meta Pixel/GA may appear after init.
    const pixelParams = collectPixelParams(this.tracker.storage);

    // Create a session ID for this batch of events
    const sessionId = this.generateSessionId();
    
    // Transform queue to match the custom API format
    const formattedEvents = queue.map((event, index) => {
      // Determine the data source
      const dataSource = queue[index];
      const dataSource_name = queue[index].interceptor || "custom";
      
      // Create event_params array from all properties in the event
      const eventParams = [];
      
      // Map all properties from the event object to event_params array
      Object.keys(event).forEach(key => {
        // Skip internal properties that start with underscore like _timestamp
        // Skip 'event' key as it's already sent as event_name
        if (key.startsWith('_') || key === 'event') return;

        const val = event[key];

        // Flatten items array — each item's properties become separate params (item_0_item_id, etc.)
        if (key === 'items' && Array.isArray(val)) {
          val.forEach((item, index) => {
            const itemObj = typeof item === 'string' ? (() => { try { return JSON.parse(item); } catch(e) { return null; } })() : item;
            if (itemObj && typeof itemObj === 'object') {
              Object.keys(itemObj).forEach(itemKey => {
                const coerced = this.tracker.eventUtils.coerceValue(itemObj[itemKey]);
                if (coerced !== null) {
                  eventParams.push({ key: `item_${index}_${itemKey}`, value: coerced });
                }
              });
            }
          });
          return;
        }

        const coerced = this.tracker.eventUtils.coerceValue(val);
        if (coerced !== null) {
          eventParams.push({ key, value: coerced });
        }
      });
      
      return {
        event_name: event.event || "bulk_event",
        event_date: event._timestamp || new Date().toISOString(),
        event_timestamp: Math.floor(Date.now() / 1000),
        created_at: Math.floor(Date.now() / 1000), // Second timestamp
        event_params: eventParams,
        session_id: event._sessionId || sessionId,
        user_first_timestamp: this.tracker.storage.getFirstTimestamp(),
        user_id: event._userId || this.tracker.userId.getId(),
        user_params: [
          this.createEventProperty('app_identifier', this.getAppIdentifier()),
          // Device fingerprint stays stable even after setUserId — the
          // logged-in identity lives in user_id, not here.
          this.createEventProperty('fingerprint_id', this.tracker.userId.getFingerprintId()),
          // id_status reflects the SENT user_id: a locked _userId uses its
          // stored flag; otherwise (resolved at send) use the live state. Events
          // held until the id was ready therefore aren't mis-flagged.
          (event._userId ? event._idIsTemporary : this.tracker.userId.isIdTemporary()) && this.createEventProperty('id_status', 'temporary'),
          this.createEventProperty('user_agent', browserInfo.user_agent),
          this.createEventProperty('language', browserInfo.language),
          this.createEventProperty('platform', browserInfo.platform),
          this.createEventProperty('screen_resolution', browserInfo.screen.split('x').slice(0, 2).join('x')),
          this.createEventProperty('color_depth', browserInfo.screen.split('x')[2]),
          this.createEventProperty('timezone', browserInfo.timezone),
          this.createEventProperty('timezone_offset', browserInfo.timezone_offset),
          browserInfo.hardware_concurrency && this.createEventProperty('hardware_concurrency', browserInfo.hardware_concurrency),
          browserInfo.device_memory && this.createEventProperty('device_memory', browserInfo.device_memory),
          this.createEventProperty('is_touch_capable', browserInfo.touch_support),
          browserInfo.webgl_vendor_renderer && this.createEventProperty('webgl_vendor', browserInfo.webgl_vendor_renderer),
          browserInfo.hash_of_canvas_fingerprint && this.createEventProperty('canvas_hash', browserInfo.hash_of_canvas_fingerprint),
          pixelParams.$fbp && this.createEventProperty('$fbp', pixelParams.$fbp),
          pixelParams.$fbc && this.createEventProperty('$fbc', pixelParams.$fbc),
          pixelParams.$gclid && this.createEventProperty('$gclid', pixelParams.$gclid),
          pixelParams.$ga_client_id && this.createEventProperty('$ga_client_id', pixelParams.$ga_client_id),
          ...this.tracker.getAttributionUserParams().map(p => this.createEventProperty(p.key, p.value)),
          ...this.getProfileProperties()
        ].filter(Boolean)
      };
    });
    
    const payload = JSON.stringify(formattedEvents);
    
    const headers = {
      "Content-Type": "application/json",
      "x-api-key": this.tracker.config.apiKey
    };
    
    // Use sendBeacon for unload events (page close, tab close, navigation)
    if (useBeacon) {
      try {
        // First try fetch with keepalive (supports custom headers unlike sendBeacon)
        if (typeof fetch !== 'undefined') {
          try {
            fetch(this.tracker.config.apiUrl, {
              method: "POST",
              headers: headers,
              body: payload,
              keepalive: true // Allows request to outlive the page
            }).catch(() => {}); // Silently fail - page is closing anyway
            
            this.tracker.logger.log("Bulk events sent via fetch keepalive");
            this.tracker.storage.clearBulkQueue();
            return;
          } catch (fetchError) {
            // fetch with keepalive failed, fall through to sendBeacon
            this.tracker.logger.log("fetch keepalive failed, trying sendBeacon");
          }
        }
        
        // Fallback to sendBeacon (no custom headers, but reliable on page close)
        if (navigator.sendBeacon) {
          // Append api key to URL since sendBeacon can't send custom headers
          const beaconUrl = new URL(this.tracker.config.apiUrl);
          beaconUrl.searchParams.set('api_key', this.tracker.config.apiKey);
          
          const blob = new Blob([payload], { type: 'application/json' });
          const success = navigator.sendBeacon(beaconUrl.toString(), blob);
          
          if (success) {
            this.tracker.logger.log("Bulk events sent successfully via Beacon");
            this.tracker.storage.clearBulkQueue();
          } else {
            this.tracker.logger.error("Failed to send bulk events via Beacon");
          }
        }
      } catch (e) {
        this.tracker.logger.error("Error sending bulk events via Beacon:", e);
      }
      return;
    }
    
    // Otherwise use fetch API
    this._sendInFlight = true;
    fetch(this.tracker.config.apiUrl, {
      method: "POST",
      headers: headers,
      body: payload
    })
    .then(response => {
      if (!response.ok) {
        throw new Error("Server returned " + response.status);
      }
      
      // Check if response has content
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return response.json().catch(() => {
          // If JSON parsing fails, return empty object
          this.tracker.logger.log("Response is not valid JSON, but status is OK");
          return {};
        });
      } else {
        // No JSON content, return empty object
        this.tracker.logger.log("Response has no JSON content, but status is OK");
        return Promise.resolve({});
      }
    })
    .then(data => {
      this.tracker.logger.log("Bulk events sent successfully:", data);
      this.tracker.storage.clearBulkQueue();
      this.tracker.retryCount = 0;
    })
    .catch(error => {
      this.tracker.logger.error("Error sending bulk events:", error);
      
      // Check if this is a permanent error (4xx errors except 429)
      const isPermanentError = error.message && (
        error.message.includes("401") ||  // Unauthorized
        error.message.includes("403") ||  // Forbidden
        error.message.includes("404") ||  // Not Found
        error.message.includes("400")     // Bad Request
      );
      
      // Clear queue for permanent errors only
      if (isPermanentError) {
        this.tracker.logger.log("Clearing bulk queue due to permanent error");
        this.tracker.storage.clearBulkQueue();
        this.tracker.retryCount = 0;
        return;
      }
      
      // Implement retry logic for temporary errors only
      if (this.tracker.retryCount < this.tracker.config.maxRetries) {
        this.tracker.retryCount++;
        
        // Exponential backoff: 2s, 4s, 8s, 16s… with jitter
        const baseDelay = Math.min(2000 * Math.pow(2, this.tracker.retryCount - 1), 60000);
        const jitter = Math.random() * 1000;
        const delay = baseDelay + jitter;
        
        setTimeout(() => {
          this.tracker.logger.log(`Retrying bulk event send. Attempt ${this.tracker.retryCount} (delay: ${Math.round(delay)}ms)`);
          this.sendEvents();
        }, delay);
      } else {
        // Max retries reached - clear queue to prevent infinite accumulation
        this.tracker.logger.log("Max retries reached, clearing bulk queue");
        this.tracker.storage.clearBulkQueue();
        this.tracker.retryCount = 0;
      }
    })
    .finally(() => { this._sendInFlight = false; });
  }
}